/**
 * Shared sign-out helpers — keep logout smooth and race-free.
 *
 * The signing-out flag is **ephemeral** (TTL). A stuck "1" must never leave
 * SignOutScreen permanent after a failed navigation or abandoned flow.
 */

import { clearCachedClient } from './client-auth-cache'

export const SIGNING_OUT_KEY = 'mechagora:signing_out'
/**
 * Max time SignOutScreen may stay up (ms). Bank-speed sign-out hard-navs
 * immediately; this is only a stuck-overlay safety net.
 */
export const SIGNING_OUT_TTL_MS = 2500

export type LoginAudience = 'client' | 'employee'

export type HardNavigateLoginOptions = {
  /** Path (+ query) to resume after re-auth. Defaults to current location when force-expiry. */
  returnTo?: string | null
  /** Login page error code (e.g. session_expired) */
  error?: string | null
}

export function markSigningOut(): void {
  if (typeof window === 'undefined') return
  try {
    // Store start timestamp — not a bare "1" that lives forever
    sessionStorage.setItem(SIGNING_OUT_KEY, String(Date.now()))
  } catch {
    /* private mode */
  }
}

export function clearSigningOutFlag(): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.removeItem(SIGNING_OUT_KEY)
  } catch {
    /* private mode */
  }
}

/**
 * True only while an active sign-out is in flight and within TTL.
 * Expired or missing → false (and stale keys are cleaned).
 */
export function isSigningOut(): boolean {
  if (typeof window === 'undefined') return false
  try {
    const raw = sessionStorage.getItem(SIGNING_OUT_KEY)
    if (!raw) return false

    // Legacy bare "1" from older builds — treat as expired immediately
    if (raw === '1') {
      sessionStorage.removeItem(SIGNING_OUT_KEY)
      return false
    }

    const started = Number(raw)
    if (!Number.isFinite(started) || started <= 0) {
      sessionStorage.removeItem(SIGNING_OUT_KEY)
      return false
    }

    const age = Date.now() - started
    if (age < 0 || age > SIGNING_OUT_TTL_MS) {
      sessionStorage.removeItem(SIGNING_OUT_KEY)
      return false
    }
    return true
  } catch {
    return false
  }
}

/** Browser storage keys that can resurrect a session after cookie clear */
const CLIENT_AUTH_STORAGE_KEYS = [
  'userType',
  'sessionToken',
  'session_token',
  'accessToken',
  'access_token',
  'refreshToken',
  'refresh_token',
  'app_token_info',
  'session_provisional_token',
  'session_mfa_user_id',
  'remember_me',
  'last_login',
  'user',
  'permissions',
  'preferences',
] as const

export function clearClientAuthStorage(): void {
  if (typeof window === 'undefined') return

  clearCachedClient()

  for (const key of CLIENT_AUTH_STORAGE_KEYS) {
    try {
      localStorage.removeItem(key)
    } catch {
      /* no-op */
    }
    try {
      // SIGNING_OUT_KEY is not in CLIENT_AUTH_STORAGE_KEYS — leave it alone
      sessionStorage.removeItem(key)
    } catch {
      /* no-op */
    }
  }

  // Best-effort non-httpOnly cookie drop (server logout clears httpOnly).
  // Do not expire csrf_token — it is not a session credential. Clearing it
  // races the login form and forces a full page reload to sign back in.
  try {
    // SOC2 session termination: drop session + OIDC AT (httpOnly cleared server-side too)
    const names = [
      'sessionToken',
      'clientSessionToken',
      'accessToken',
      'refreshToken',
      'userRole',
      'zitadelSessionId',
      'portalAudience',
      'oidc_audience',
    ]
    for (const name of names) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax`
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;Max-Age=0`
    }
  } catch {
    /* no-op */
  }
}

/**
 * Best-effort BFF cookie logout. Fire-and-forget for bank-speed leave.
 * Prefer sendBeacon so Set-Cookie clears can complete after hard nav.
 * Does not throw; short fetch timeout only if beacon unavailable.
 */
export async function postLogoutApi(timeoutMs = 400): Promise<void> {
  if (typeof window === 'undefined') return

  const body = JSON.stringify({ reason: 'client_sign_out' })

  // Prefer keepalive beacon so cookie clear outlives the document unload
  try {
    const ok = navigator.sendBeacon?.(
      '/api/auth/logout',
      new Blob([body], { type: 'application/json' })
    )
    if (ok) return
  } catch {
    /* fall through to fetch */
  }

  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), timeoutMs)

  try {
    // Do not await long — caller navigates immediately; keepalive continues
    void fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body,
      signal: controller.signal,
    }).catch(() => {
      /* Network/timeout — local clear already done; hard nav follows */
    })
  } catch {
    /* no-op */
  } finally {
    window.clearTimeout(timer)
  }
}

function sanitizeReturnTo(raw: string | null | undefined): string | null {
  if (!raw) return null
  const trimmed = raw.trim()
  if (!trimmed.startsWith('/')) return null
  // Block open redirects / protocol-relative
  if (trimmed.startsWith('//')) return null
  if (trimmed.startsWith('/auth')) return null
  if (trimmed === '/login' || trimmed.startsWith('/login?') || trimmed.startsWith('/login/')) {
    return null
  }
  if (trimmed === '/signin' || trimmed.startsWith('/signin?') || trimmed.startsWith('/signin/')) {
    return null
  }
  return trimmed.slice(0, 512)
}

/** Canonical client login path + query (soft or hard navigation). */
export function clientLoginHref(opts?: HardNavigateLoginOptions): string {
  const params = new URLSearchParams()
  const returnTo = sanitizeReturnTo(opts?.returnTo)
  if (returnTo) {
    // Login pages read return_to; middleware historically used returnTo — set both.
    params.set('return_to', returnTo)
    params.set('returnTo', returnTo)
  }
  if (opts?.error) {
    params.set('error', opts.error)
  }
  const qs = params.toString()
  return qs ? `/auth/login?${qs}` : '/auth/login'
}

/**
 * Navigate to login with a full document load so React shell/guard state
 * cannot re-run session verify against a half-cleared store.
 * Uses replace (no history stack) — bank / brokerage pattern.
 */
export function hardNavigateToLogin(
  _audience: LoginAudience = 'client',
  opts?: HardNavigateLoginOptions
): void {
  if (typeof window === 'undefined') return
  window.location.replace(clientLoginHref(opts))
}

/**
 * Await BFF cookie clear so the login form can mint CSRF against a stable jar.
 * Used on the login page after session expiry — not on the bank-speed leave path.
 */
export async function awaitPostLogoutApi(timeoutMs = 2000): Promise<void> {
  if (typeof window === 'undefined') return

  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), timeoutMs)

  try {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: 'client_reauth' }),
      signal: controller.signal,
    })
  } catch {
    /* Network/timeout — login CSRF retry still recovers */
  } finally {
    window.clearTimeout(timer)
  }
}

export type ForceSessionExpiredOptions = {
  returnTo?: string | null
  /** Defaults to session_expired */
  error?: string
  /** Skip if already on public/auth routes */
  skipIfPublic?: boolean
}

/**
 * Bank-speed auto sign-out when the session is known dead (JWT exp, verify 401, etc.).
 * Idempotent under the signing-out TTL.
 */
export function forceClientSessionExpiredSignOut(opts?: ForceSessionExpiredOptions): void {
  if (typeof window === 'undefined') return

  const path = window.location.pathname || '/'
  if (opts?.skipIfPublic !== false) {
    if (
      path.startsWith('/auth') ||
      path === '/login' ||
      path === '/signin' ||
      path === '/' ||
      path.startsWith('/pricing') ||
      path.startsWith('/legal') ||
      path.startsWith('/journal') ||
      path.startsWith('/trading/preview') ||
      path.startsWith('/terms') ||
      path.startsWith('/privacy')
    ) {
      return
    }
  }

  if (isSigningOut()) {
    hardNavigateToLogin('client', {
      returnTo: opts?.returnTo ?? `${path}${window.location.search || ''}`,
      error: opts?.error ?? 'session_expired',
    })
    return
  }

  markSigningOut()
  try {
    clearClientAuthStorage()
  } catch {
    /* no-op */
  }
  void postLogoutApi(400)

  const returnTo =
    opts?.returnTo ??
    (typeof window !== 'undefined' ? `${path}${window.location.search || ''}` : null)

  hardNavigateToLogin('client', {
    returnTo,
    error: opts?.error ?? 'session_expired',
  })
}

/** Dispatch browser event consumed by AuthProvider (keeps one UI notification path). */
export function dispatchSessionExpiredEvent(message = 'Your session has expired. Please sign in again.'): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent('session-expired', {
      detail: {
        message,
        timestamp: new Date().toISOString(),
      },
    })
  )
}
